import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { ChevronDown } from 'lucide-react';
import { useEffect, useState } from 'react';

export default function AdditionalTagsForSmartAssistant({ categories, onSelectionChange }) {
    const [selectedCategory, setSelectedCategory] = useState('Calendar');
    const [selectedSubcategory, setSelectedSubcategory] = useState('Troubleshooting');

    const getCategoryStorageKey = () => 'selectedCategory_smart-assistant';
    const getSubcategoryStorageKey = () => 'selectedSubcategory_smart-assistant';

    useEffect(() => {
        if (categories.length) {
            const categoryStorageKey = getCategoryStorageKey();
            const subcategoryStorageKey = getSubcategoryStorageKey();

            // Try to load persisted selections
            const persistedCategory = localStorage.getItem(categoryStorageKey);
            const persistedSubcategory = localStorage.getItem(subcategoryStorageKey);

            // Find persisted category in current categories array
            const foundCategory = persistedCategory
                ? categories.find(cat => cat.category === persistedCategory)
                : null;

            // Use persisted category if found, otherwise default to first
            const categoryToSelect = foundCategory || categories[0];
            const availableSubcategories = categoryToSelect.subcategories.map(s => s.subcategory);

            // For subcategory, check if persisted subcategory exists in current category
            const subcategoryToSelect = persistedSubcategory && availableSubcategories.includes(persistedSubcategory)
                ? persistedSubcategory
                : availableSubcategories[0] || '';

            setSelectedCategory(categoryToSelect.category);
            setSelectedSubcategory(subcategoryToSelect);

            // Persist the selections
            localStorage.setItem(categoryStorageKey, categoryToSelect.category);
            if (subcategoryToSelect) {
                localStorage.setItem(subcategoryStorageKey, subcategoryToSelect);
            }

            if (onSelectionChange) {
                onSelectionChange(categoryToSelect.category, subcategoryToSelect);
            }
        }
    }, [categories]);

    const handleCategoryChange = (category) => {
        setSelectedCategory(category);
        const foundCat = categories.find((c) => c.category === category);
        const firstSub = foundCat?.subcategories[0]?.subcategory || '';
        setSelectedSubcategory(firstSub);

        // Persist selections
        localStorage.setItem(getCategoryStorageKey(), category);
        localStorage.setItem(getSubcategoryStorageKey(), firstSub);

        if (onSelectionChange) {
            onSelectionChange(category, firstSub);
        }
    };

    const handleSubcategoryChange = (sub) => {
        setSelectedSubcategory(sub);

        // Persist subcategory selection
        localStorage.setItem(getSubcategoryStorageKey(), sub);

        if (onSelectionChange) {
            onSelectionChange(selectedCategory, sub);
        }
    };

    const availableSubcategories =
        categories
            .find((cat) => cat.category === selectedCategory)
            ?.subcategories.map((s) => s.subcategory) || [];

    return (
        <div className="w-full max-w-4xl mx-auto space-y-4 flex flex-row gap-4 mt-4">
      {/* Category Dropdown */}
      <div className="w-full sm:w-[200px]">
        <Listbox value={selectedCategory} onChange={handleCategoryChange}>
          <div className="relative">
            <ListboxButton className="text-sm w-full flex justify-between items-center px-3 py-2 bg-white/20 rounded-md text-white border border-white/30">
              <span className="truncate">{selectedCategory || 'Select Category'}</span>
              <ChevronDown size={18} className="flex-shrink-0 ml-2" />
            </ListboxButton>
            <ListboxOptions 
              className="absolute z-50 w-fit sm:max-w-[200px] bg-white/40 outline-none p-1.5 rounded backdrop-blur-2xl shadow-lg max-h-60 overflow-y-auto
                scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/30 hover:scrollbar-thumb-white/50
                [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-white/50"
              anchor="bottom start"
              transition
            >
              {categories.map((cat) => (
                <ListboxOption className="flex rounded" key={cat.category} value={cat.category}>
                  {({ selected, active }) => (
                    <span
                      className={`w-full rounded px-2 py-1 cursor-pointer transition-colors ${
                        active ? 'bg-black/60 text-white' : 'text-gray-200'
                      } ${selected ? 'font-medium text-white' : ''}`}
                    >
                      {cat.category}
                    </span>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
      </div>

      {/* Subcategory Dropdown */}
      <div className="w-full sm:w-[200px]">
        <Listbox value={selectedSubcategory} onChange={handleSubcategoryChange}>
          <div className="relative">
            <ListboxButton className="text-sm w-full flex justify-between items-center px-3 py-2 bg-white/20 rounded-md text-white disabled:opacity-50 border border-white/30">
              <span className="truncate">{selectedSubcategory || 'Select Subcategory'}</span>
              <ChevronDown size={18} className="flex-shrink-0 ml-2" />
            </ListboxButton>
            <ListboxOptions 
              className="absolute z-50 w-fit sm:max-w-[200px] bg-white/40 outline-none p-1.5 rounded backdrop-blur-2xl shadow-lg max-h-60 overflow-y-auto
                scrollbar-thin scrollbar-track-transparent scrollbar-thumb-white/30 hover:scrollbar-thumb-white/50
                [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-white/30 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb:hover]:bg-white/50"
              anchor="bottom start"
              transition
            >
              {availableSubcategories.map((sub) => (
                <ListboxOption className="flex rounded" key={sub} value={sub}>
                  {({ selected, active }) => (
                    <span
                      className={`w-full rounded px-2 py-1 cursor-pointer transition-colors ${
                        active ? 'bg-black/60 text-white' : 'text-gray-200'
                      } ${selected ? 'font-medium text-white' : ''}`}
                    >
                      {sub}
                    </span>
                  )}
                </ListboxOption>
              ))}
            </ListboxOptions>
          </div>
        </Listbox>
      </div>
    </div>
    );
}